
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function BookPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    fromDate: '',
    toDate: '',
    location: '',
    depositAvailable: ''
  });

  const [depositAmount, setDepositAmount] = useState(0);
  const [isLocationsOpen, setIsLocationsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      const fromDate = new Date(formData.fromDate);
      const toDate = new Date(formData.toDate);
      const timeDiff = toDate.getTime() - fromDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      
      if (daysDiff > 0) {
        const calculatedDeposit = daysDiff * 3000;
        setDepositAmount(calculatedDeposit);
      } else {
        setDepositAmount(0);
      }
    } else {
      setDepositAmount(0);
    }
  }, [formData.fromDate, formData.toDate]);

  // Add structured data for SEO
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Book a Beach Buggy - Beach Bugs Cape Town",
      "description": "Book your premium beach buggy rental in Cape Town. R3,000 per day with comprehensive insurance coverage.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/book`,
      "mainEntity": {
        "@type": "Service",
        "name": "Beach Buggy Rental Booking",
        "description": "Premium beach buggy rental service with instant booking",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Beach Bugs",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Cape Town",
            "addressRegion": "Western Cape",
            "addressCountry": "ZA"
          }
        }
      }
    };

    // Remove existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Calculate rental days and total amount
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    const timeDiff = toDate.getTime() - fromDate.getTime();
    const rentalDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    const totalAmount = rentalDays * 3000;
    
    const params = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => {
      params.append(key, value);
    });
    params.append('rentalDays', rentalDays.toString());
    params.append('totalAmount', `R${totalAmount.toLocaleString()}`);
    params.append('dailyRate', 'R3,000');

    try {
      const response = await fetch('https://readdy.ai/api/form/d4326o1eds0ugd973hmg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      });
      
      if (response.ok) {
        alert('Thank you, we\'ll be in contact IF we have a beach buggy available. They are all booked out 99% of the time. Thank you for understanding.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          fromDate: '',
          toDate: '',
          location: '',
          depositAvailable: ''
        });
      } else {
        alert('There was an error submitting your request. Please try again.');
      }
    } catch (error) {
      alert('There was an error submitting your request. Please try again.');
    }
  };

  const features = [
    { icon: 'ri-car-line', title: 'Premium Beach Buggies', description: 'Top-quality vehicles maintained to perfection' },
    { icon: 'ri-shield-check-line', title: 'Full Insurance Coverage', description: 'Comprehensive protection up to R40,000' },
    { icon: 'ri-time-line', title: 'Flexible Booking', description: 'Minimum 2 days @ R3,000 per day' },
    { icon: 'ri-customer-service-2-line', title: '24/7 Support', description: 'Round-the-clock assistance during rental' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <nav className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 sm:h-20">
            <div className="flex items-center space-x-3 min-w-0 flex-shrink-0">
              <a className="flex items-center space-x-3" href="/" data-discover="true">
                <img 
                  src="https://static.readdy.ai/image/5c2d16bce98820aab16a3b52f2f5cfd8/71f081b0c967dd005308d1aa03cdf9ab.png"
                  alt="Beach Bugs Logo"
                  className="w-[180px] sm:w-[250px] h-auto object-contain flex-shrink-0"
                />
              </a>
            </div>
            <div className="hidden lg:block flex-1 max-w-md mx-8">
              <div className="flex items-center justify-center space-x-8">
                <a className="text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-gray-600 hover:text-[#e9f638] relative group" href="/" data-discover="true">
                  Home
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#e9f638] transition-all duration-300 group-hover:w-full"></span>
                </a>
                <div className="relative">
                  <button 
                    className="text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-gray-600 hover:text-[#e9f638] relative group flex items-center"
                    onClick={() => setIsLocationsOpen(!isLocationsOpen)}
                  >
                    Locations
                    <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-300 ${isLocationsOpen ? 'rotate-180' : ''}`}></i>
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#e9f638] transition-all duration-300 group-hover:w-full"></span>
                  </button>
                  {isLocationsOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <a href="/bloubergstrand" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Bloubergstrand
                      </a>
                      <a href="/melkbosstrand" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Melkbosstrand
                      </a>
                      <a href="/camps-bay" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Camps Bay
                      </a>
                      <a href="/green-point" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Green Point
                      </a>
                      <a href="/sea-point" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Sea Point
                      </a>
                      <a href="/table-view" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Table View
                      </a>
                      <a href="/stilbaai" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Stilbaai
                      </a>
                      <a href="/jeffreys-bay" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Jeffreys Bay
                      </a>
                      <a href="/wilderness" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Wilderness
                      </a>
                      <a href="/george" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        George
                      </a>
                      <a href="/langebaan" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Langebaan
                      </a>
                    </div>
                  )}
                </div>
                <a className="text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-black hover:text-[#e9f638] relative group" href="/book" data-discover="true">
                  Book Now
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#e9f638] transition-all duration-300 group-hover:w-full"></span>
                </a>
              </div>
            </div>
            <div className="hidden lg:block flex-shrink-0">
              <a className="bg-[#e9f638] text-black px-4 xl:px-6 py-2 xl:py-3 rounded-full text-sm font-semibold hover:bg-[#d4e032] transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" href="#booking" data-discover="true">Book a Bug!</a>
            </div>
            <div className="lg:hidden flex-shrink-0">
              <button 
                className="inline-flex items-center justify-center p-2 rounded-xl text-gray-600 hover:text-black hover:bg-gray-100 cursor-pointer transition-all duration-300"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl sm:text-2xl transition-all duration-300`}></i>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-xl z-40">
              <div className="px-4 py-6 space-y-4">
                <a 
                  className="block text-lg font-medium text-gray-600 hover:text-[#e9f638] transition-colors duration-300 py-2" 
                  href="/" 
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </a>
                <a 
                  className="block text-lg font-medium text-black hover:text-[#e9f638] transition-colors duration-300 py-2" 
                  href="/book" 
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Book Now
                </a>
                <div className="space-y-2">
                  <div className="text-lg font-medium text-gray-600 py-2">Locations</div>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/bloubergstrand"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Bloubergstrand
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/melkbosstrand"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Melkbosstrand
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/camps-bay"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Camps Bay
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/green-point"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Green Point
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/sea-point"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Sea Point
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/table-view"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Table View
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/stilbaai"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Stilbaai
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/jeffreys-bay"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Jeffreys Bay
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/wilderness"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Wilderness
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/george"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    George
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/langebaan"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Langebaan
                  </a>
                </div>
                <div className="pt-4">
                  <a 
                    className="block w-full bg-[#e9f638] text-black px-6 py-3 rounded-full text-lg font-semibold hover:bg-[#d4e032] transition-all duration-300 text-center shadow-lg" 
                    href="#booking"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Book a Bug!
                  </a>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white py-12 sm:py-16 lg:py-20 min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              alt="Matte black beach buggy with two blonde girls driving on coastal road" 
              className="w-full h-full object-cover object-top" 
              src="https://public.readdy.ai/ai/img_res/728010cc68acd8504ad0a6bf70631726.jpg"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-4xl">
              <div className="space-y-6 sm:space-y-8">
                <div className="space-y-4">
                  <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-black/80 backdrop-blur-sm rounded-full border border-[#e9f638]/30">
                    <i className="ri-calendar-check-line text-[#e9f638] mr-2 text-sm sm:text-base"></i>
                    <span className="text-white font-medium text-sm sm:text-base">Quick & Easy Booking</span>
                  </div>
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                    Book Your <span className="bg-gradient-to-r from-[#e9f638] to-[#d4e032] bg-clip-text text-transparent">Beach Buggy</span>
                  </h1>
                  <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed max-w-2xl">
                    Reserve your premium beach buggy adventure today. R3,000 per day with comprehensive insurance coverage.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Booking Section */}
        <section id="booking" className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-20 items-start">
              {/* Left Side - Features */}
              <div className="space-y-8 sm:space-y-10">
                <div className="space-y-4 sm:space-y-6">
                  <div className="inline-flex items-center px-4 py-2 bg-black rounded-full mb-4">
                    <i className="ri-award-line text-[#e9f638] mr-2"></i>
                    <span className="text-white font-medium text-sm sm:text-base">Premium Service</span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black leading-tight">Why Choose Beach Bugs?</h2>
                  <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">Experience the ultimate coastal adventure with our premium beach buggy rental service.</p>
                </div>
                
                <div className="space-y-6 sm:space-y-8">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-4 sm:space-x-6 group">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#e9f638] to-[#d4e032] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                        <i className={`${feature.icon} text-black text-lg sm:text-xl`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold text-black mb-2 group-hover:text-[#e9f638] transition-colors duration-300">{feature.title}</h3>
                        <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 sm:p-8 border border-gray-100">
                  <h3 className="text-xl sm:text-2xl font-bold text-black mb-4">Rental Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Daily Rate:</span>
                      <span className="font-bold text-[#e9f638] text-lg">R3,000</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Minimum Rental:</span>
                      <span className="font-semibold text-black">2 Days</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Insurance Coverage:</span>
                      <span className="font-semibold text-black">Up to R40,000</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Service Hours:</span>
                      <span className="font-semibold text-black">8:00 - 18:00</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Booking Form */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-gray-100 sticky top-24">
                <div className="text-center mb-8 sm:mb-10">
                  <div className="inline-flex items-center px-4 py-2 bg-black rounded-full mb-4">
                    <i className="ri-calendar-check-line text-[#e9f638] mr-2"></i>
                    <span className="text-white font-medium text-sm sm:text-base">Instant Booking</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-black mb-3">Reserve Your Bug</h2>
                  <p className="text-gray-600 text-sm sm:text-base">Complete the form below to check availability and rates</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-readdy-form>
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Telephone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      placeholder="Your phone number"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="fromDate" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">From *</label>
                      <input
                        type="date"
                        id="fromDate"
                        name="fromDate"
                        value={formData.fromDate}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      />
                    </div>
                    <div>
                      <label htmlFor="toDate" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">To *</label>
                      <input
                        type="date"
                        id="toDate"
                        name="toDate"
                        value={formData.toDate}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Where do you need it? *</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      placeholder="Delivery location"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                      {depositAmount > 0 ? `Do you have R${depositAmount.toLocaleString()} available? *` : 'Do you have the calculated amount available? *'}
                    </label>
                    {depositAmount > 0 && (
                      <p className="text-xs text-gray-500 mb-3">
                        Calculation: {Math.ceil((new Date(formData.toDate).getTime() - new Date(formData.fromDate).getTime()) / (1000 * 3600 * 24))} days × R3,000 = R{depositAmount.toLocaleString()}
                      </p>
                    )}
                    <div className="space-y-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="depositAvailable"
                          value="yes"
                          checked={formData.depositAvailable === 'yes'}
                          onChange={handleInputChange}
                          required
                          className="mr-3 text-[#e9f638] focus:ring-[#e9f638]"
                        />
                        <span className="text-sm text-gray-700">Yes, I have the amount available</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="depositAvailable"
                          value="no"
                          checked={formData.depositAvailable === 'no'}
                          onChange={handleInputChange}
                          required
                          className="mr-3 text-[#e9f638] focus:ring-[#e9f638]"
                        />
                        <span className="text-sm text-gray-700">No, I don't have the amount available</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#e9f638] text-black py-4 sm:py-5 rounded-xl text-base sm:text-lg font-bold hover:bg-[#d4e032] transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Check rates & availability
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-[#e9f638] via-[#d4e032] to-[#c5cc2a] relative overflow-hidden">
          <div className="absolute inset-0 bg-black/5"></div>
          <div className="absolute top-10 right-10 w-16 sm:w-32 h-16 sm:h-32 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-10 left-10 w-12 sm:w-24 h-12 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">Questions About Your Booking?</h2>
            <p className="text-lg sm:text-xl text-white/90 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed">
              Our team is here to help you plan the perfect beach buggy adventure. Contact us for any questions about availability, locations, or special requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="/" 
                className="bg-white text-black px-8 sm:px-10 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer inline-block shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
              >
                Back to Home
              </a>
              <a 
                href="#booking" 
                className="bg-black text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer inline-block shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
              >
                Book Another Bug
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12">
            <div className="col-span-1 sm:col-span-2 lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6 sm:mb-8">
                <img 
                  src="https://static.readdy.ai/image/5c2d16bce98820aab16a3b52f2f5cfd8/71f081b0c967dd005308d1aa03cdf9ab.png"
                  alt="Beach Bugs Logo"
                  className="w-[250px] h-auto object-contain flex-shrink-0"
                />
              </div>
              <p className="text-gray-300 mb-8 sm:mb-10 max-w-md text-base sm:text-lg leading-relaxed">
                South Africa's premier beach buggy rental service. Experience unforgettable coastal adventures with our premium fleet and comprehensive insurance coverage.
              </p>
              <div className="flex space-x-4 sm:space-x-6">
                <a href="#" className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 rounded-xl flex items-center justify-center text-gray-300 hover:text-[#e9f638] hover:bg-gray-700 transition-all duration-300 cursor-pointer" rel="nofollow">
                  <i className="ri-facebook-fill text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 rounded-xl flex items-center justify-center text-gray-300 hover:text-[#e9f638] hover:bg-gray-700 transition-all duration-300 cursor-pointer" rel="nofollow">
                  <i className="ri-instagram-line text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 rounded-xl flex items-center justify-center text-gray-300 hover:text-[#e9f638] hover:bg-gray-700 transition-all duration-300 cursor-pointer" rel="nofollow">
                  <i className="ri-twitter-line text-lg sm:text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Locations</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/bloubergstrand" className="hover:text-white transition-colors">Bloubergstrand</Link></li>
                <li><Link to="/melkbosstrand" className="hover:text-white transition-colors">Melkbosstrand</Link></li>
                <li><Link to="/camps-bay" className="hover:text-white transition-colors">Camps Bay</Link></li>
                <li><Link to="/green-point" className="hover:text-white transition-colors">Green Point</Link></li>
                <li><Link to="/sea-point" className="hover:text-white transition-colors">Sea Point</Link></li>
                <li><Link to="/table-view" className="hover:text-white transition-colors">Table View</Link></li>
                <li><Link to="/jeffreys-bay" className="hover:text-white transition-colors">Jeffreys Bay</Link></li>
                <li><Link to="/stilbaai" className="hover:text-white transition-colors">Stilbaai</Link></li>
                <li><Link to="/wilderness" className="hover:text-white transition-colors">Wilderness</Link></li>
                <li><Link to="/george" className="hover:text-white transition-colors">George</Link></li>
                <li><Link to="/langebaan" className="hover:text-white transition-colors">Langebaan</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg sm:text-xl font-bold mb-6 sm:mb-8 text-white">Contact Info</h4>
              <div className="space-y-4 sm:space-y-5 text-gray-300">
                <div className="flex items-center space-x-3">
                  <i className="ri-mail-line text-[#e9f638] text-base sm:text-lg flex-shrink-0"></i>
                  <span className="text-sm sm:text-base break-all">info@beachbugs.co.za</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-map-pin-line text-[#e9f638] text-base sm:text-lg flex-shrink-0"></i>
                  <span className="text-sm sm:text-base">South Africa</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-time-line text-[#e9f638] text-base sm:text-lg flex-shrink-0"></i>
                  <span className="text-sm sm:text-base">Daily: 8:00-18:00</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 sm:pt-10 mt-12 sm:mt-16 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-gray-600 text-xs sm:text-sm text-center sm:text-left">© 2024 Beach Bugs. All rights reserved.</p>
            <div className="flex items-center space-x-4 text-xs sm:text-sm">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="nofollow noopener noreferrer" className="text-gray-600 hover:text-gray-400 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
